import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { useCallback, useMemo, useState } from "react";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import agent from "../../../agent";
import styles from "../style.module.scss";

const ReadonlyField = ({ label, value }) => (
    <div className={styles.readonlyField}>
        <div className={styles.readonlyLabel}>{label}</div>
        <div className={styles.readonlyValue}>{value ?? "-"}</div>
    </div>
);

const ObligationDetailsModal = ({ visible, obligationId, onHide }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);

    const fetchDetails = useCallback(async () => {
        if (!visible || !obligationId) return;

        setLoading(true);
        try {
            const data = await agent.ObligationsJournal.details(obligationId);
            setDetails(data ?? null);
        } finally {
            setLoading(false);
        }
    }, [visible, obligationId]);

    useAsyncEffect(fetchDetails, [fetchDetails]);

    const header = useMemo(() => {
        return (
            <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>Детализация</div>
                <div className={styles.modalSubTitle}>ID обязательства: {obligationId ?? "-"}</div>
            </div>
        );
    }, [obligationId]);

    const footer = (
        <div className={styles.modalFooter}>
            <Button label="Закрыть" onClick={onHide} />
        </div>
    );

    const person = details?.person ?? {};
    const withholding = details?.withholding ?? {};
    const document = details?.primaryDocument ?? {};
    const logs = details?.logs ?? {};

    return (
        <Dialog
            header={header}
            visible={visible}
            onHide={onHide}
            footer={footer}
            className={styles.detailsDialog}
            modal
            draggable={false}
            resizable={false}
            dismissableMask
            breakpoints={{ "960px": "90vw", "640px": "96vw" }}
            style={{ width: "860px" }}
        >
            {loading ? (
                <div className={styles.modalLoading}>
                    <ProgressSpinner />
                </div>
            ) : (
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    <TabPanel header="Физическое лицо" leftIcon="pi pi-user">
                        <div className={styles.readonlyGrid2}>
                            <ReadonlyField label="ИИН" value={person.iin} />
                            <ReadonlyField label="Номер договорной оферты" value={person.offerNumber} />

                            <ReadonlyField label="Статус водителя" value={person.status} />
                            <ReadonlyField label="Резидент" value={person.resident} />

                            <ReadonlyField label="Нерезидент" value={person.nonResident} />
                            <ReadonlyField label="Пенсионер по возрасту" value={person.pensionerByAge} />

                            <ReadonlyField label="Пенсионер по выслуге лет" value={person.pensionerByService} />
                            <ReadonlyField label="Студент" value={person.student} />

                            <ReadonlyField label="Инвалид 1,2,3 группы" value={person.disability123} />
                            <ReadonlyField label="Инвалид 1,2 группы бессрочно" value={person.disability12Forever} />

                            <ReadonlyField label="Родитель (опекун) инвалида" value={person.guardianOfDisabled} />
                            <ReadonlyField label="Стандартный вычет (базовый)" value={person.standardDeduction} />

                            <ReadonlyField label="Социальный налоговый вычет" value={person.socialTaxDeduction} />
                            <div />
                        </div>
                    </TabPanel>

                    <TabPanel header="Информация об удержании" leftIcon="pi pi-credit-card">
                        <div className={styles.readonlyGrid2}>
                            <ReadonlyField label="Базовая величина" value={withholding.baseValue} />
                            <ReadonlyField label="Размер (ставки)" value={withholding.rate} />

                            <ReadonlyField label="Сумма к удержанию" value={withholding.amountToWithhold} />
                            <ReadonlyField label="База для расчета" value={withholding.baseForCalc} />

                            <ReadonlyField label="Валюта" value={withholding.currency} />
                            <ReadonlyField label="Дата уплаты обязательства" value={withholding.paymentDueDate} />
                        </div>
                    </TabPanel>

                    <TabPanel header="Первичный документ" leftIcon="pi pi-file">
                        <div className={styles.readonlyGridDoc}>
                            <ReadonlyField label="id первичного документа" value={document.id} />
                            <ReadonlyField label="Наименование первичного документа" value={document.name} />
                            <ReadonlyField label="Номер АВР" value={document.avrNumber} />
                            <ReadonlyField label="Дата документа" value={document.documentDate} />
                            <ReadonlyField label="Статус подписания АВР" value={document.avrSignStatus} />
                            <ReadonlyField label="Дата расчета налогов" value={document.taxesCalcDate} />
                            <ReadonlyField label="Номер заказа" value={document.orderNumber} />
                        </div>

                        <div className={styles.summaryBlock}>
                            <div className={styles.summaryTitle}>Итоговая сумма начислений за период:</div>
                            <div className={styles.summaryGrid}>
                                <ReadonlyField label="Сумма на руки" value={document.netAmount} />
                                <ReadonlyField label="Итоговая стоимость" value={document.totalAmount} />
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="Данные логирования" leftIcon="pi pi-wave-pulse">
                        <div className={styles.readonlyGrid1}>
                            <ReadonlyField label="Дата утверждения" value={logs.approvedAt} />
                            <ReadonlyField label="Пользователь" value={logs.approvedBy} />
                        </div>
                    </TabPanel>
                </TabView>
            )}
        </Dialog>
    );
};

export default ObligationDetailsModal;
